/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import { bool, node, oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { buildCommissionInclusiveDiscountChain } from '../../util/cocopoolPricing';
import { indoorSpaceTitleForLineItem, isIndoorSpaceLineItem } from '../../util/indoorSpaces';
import {
  propTypes,
  LISTING_UNIT_TYPES,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_DISCOUNT_CODE,
  LINE_ITEM_WEEKDAY_BOOKING_DISCOUNT,
  LINE_ITEM_FIRST_BOOKING_WELCOME_DISCOUNT,
  LINE_ITEM_HOST_INSURANCE,
  LINE_ITEM_HOST_INSURANCE_DISCOUNT,
  LINE_ITEM_POOL_USE_EXTRA,
} from '../../util/types';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemWeekdayBookingDiscountMaybe from './LineItemWeekdayBookingDiscountMaybe';
import LineItemFirstBookingDiscountMaybe from './LineItemFirstBookingDiscountMaybe';
import LineItemDiscountCodeMaybe from './LineItemDiscountCodeMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemPoolUseExtraMaybe from './LineItemPoolUseExtraMaybe';
import LineItemIndoorSpacesMaybe from './LineItemIndoorSpacesMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemHostInsuranceMaybe from './LineItemHostInsuranceMaybe';
import LineItemHostInsuranceDiscountMaybe from './LineItemHostInsuranceDiscountMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';
import {
  parseUpgrades,
  getCommissionRates,
  computeUpgradeAmounts,
  getUpgradeLabel,
  getGroupOverride,
  getExtraHours,
} from '../../util/upgrades';
import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const numericPercentage = raw =>
  raw != null ? Number(typeof raw.valueOf === 'function' ? raw.valueOf() : raw) : null;

const isHostInsuranceCode = code =>
  code === LINE_ITEM_HOST_INSURANCE || code === LINE_ITEM_HOST_INSURANCE_DISCOUNT;

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    intl,
    dateType,
    timeZone,
    currency,
    marketplaceName,
    afterTotal,
    beforeTotal,
    showOnlyTotalByDefault,
    hideBreakdownToggle,
    itemizeSummary,
  } = props;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const [showBreakdownDetails, setShowBreakdownDetails] = React.useState(
    !showOnlyTotalByDefault || !isCustomer
  );
  const showSummaryOnly = isCustomer && showOnlyTotalByDefault && !showBreakdownDetails;
  const hideCustomerFeeDetails = isCustomer && !showBreakdownDetails;
  const lineItems = transaction.attributes.lineItems;
  const unitLineItem = lineItems?.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItem?.code;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission =
      isCustomer && !hideCustomerFeeDetails && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  // Upgrades (ampliaciones post-aceptación) guardados en metadata.upgrades.
  // Se integran en el pedido como líneas más: cada ampliación suma a un único
  // Subtotal / Tarifa de Cocopool / total. El bañista ve el importe con comisión
  // (importe_total); el anfitrión ve el precio base (sin comisión del bañista).
  // Además, el grupo y la hora de fin mostrados reflejan las ampliaciones.
  const upgrades = parseUpgrades(transaction?.attributes?.metadata);
  const { customerPct, providerPct } = getCommissionRates(transaction);
  const upgradeRows = upgrades
    .map((upgrade, index) => {
      const amounts = computeUpgradeAmounts(upgrade, { currency, customerPct, providerPct });
      if (!amounts) return null;
      return { key: index, label: getUpgradeLabel(upgrade, intl), amounts, upgrade };
    })
    .filter(Boolean);
  const hasUpgrades = upgradeRows.length > 0;
  // Group/hours shown reflect only valid (charged) upgrades — the same set that
  // appears as line items — so an entry without importe_total can't drive them.
  const validUpgrades = upgradeRows.map(row => row.upgrade);
  const groupOverride = getGroupOverride(validUpgrades);
  const extraHours = getExtraHours(validUpgrades);

  const sumUpgradeAmount = pick =>
    upgradeRows.reduce((sum, row) => sum + pick(row.amounts).amount, 0);

  // Combined totals = base reservation (from payout/payin and the provider
  // commission line item) + the upgrades.
  const payoutTotal = transaction?.attributes?.payoutTotal;
  const payinTotal = transaction?.attributes?.payinTotal;
  const totalCurrency = payoutTotal?.currency || payinTotal?.currency || currency;
  const providerCommissionLineItem = lineItems.find(
    item => item.code === LINE_ITEM_PROVIDER_COMMISSION && !item.reversal
  );
  const baseProviderCommission = providerCommissionLineItem
    ? Math.abs(providerCommissionLineItem.lineTotal.amount)
    : 0;

  // Coste neto del seguro para el anfitrión (negativo; 0 para superanfitriones, cuyo
  // descuento lo compensa). Ya está descontado en payoutTotal, así que se suma de
  // vuelta al subtotal: el seguro se muestra como línea propia debajo, igual que la
  // comisión, y de lo contrario parecería restado dos veces.
  const hostInsuranceNet = lineItems
    .filter(item => isHostInsuranceCode(item.code) && !item.reversal)
    .reduce((sum, item) => sum + item.lineTotal.amount, 0);

  const combinedProviderSubtotal =
    payoutTotal != null
      ? new Money(
          payoutTotal.amount +
            baseProviderCommission -
            hostInsuranceNet +
            sumUpgradeAmount(a => a.base),
          totalCurrency
        )
      : null;
  const combinedProviderCommission = new Money(
    -(baseProviderCommission + sumUpgradeAmount(a => a.providerFee)),
    totalCurrency
  );
  const combinedProviderNet =
    payoutTotal != null
      ? new Money(payoutTotal.amount + sumUpgradeAmount(a => a.hostNet), totalCurrency)
      : null;
  const combinedCustomerTotal =
    payinTotal != null
      ? new Money(payinTotal.amount + sumUpgradeAmount(a => a.guest), totalCurrency)
      : null;
  // Customer fee = base reservation commission + the upgrades' commission
  // (guest − base), so it stays consistent with the upgrade lines shown at base.
  const customerCommissionLineItem = lineItems.find(
    item => item.code === LINE_ITEM_CUSTOMER_COMMISSION && !item.reversal
  );
  const baseCustomerCommission = customerCommissionLineItem
    ? customerCommissionLineItem.lineTotal.amount
    : 0;
  const combinedCustomerCommission = new Money(
    baseCustomerCommission + sumUpgradeAmount(a => a.guest) - sumUpgradeAmount(a => a.base),
    totalCurrency
  );

  // Provider total label mirrors LineItemTotalPrice (received/refunded/default).
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  const upgradeProcess = processName ? getProcess(processName) : null;
  const lastTransition = transaction?.attributes?.lastTransition;
  const providerTotalMessageId =
    upgradeProcess && upgradeProcess.isCompleted(lastTransition)
      ? 'OrderBreakdown.providerTotalReceived'
      : upgradeProcess && upgradeProcess.isRefunded(lastTransition)
      ? 'OrderBreakdown.providerTotalRefunded'
      : 'OrderBreakdown.providerTotalDefault';

  const classes = classNames(rootClassName || css.root, className);
  const baseUnitMessageIdByCode = {
    'line-item/hour': 'OrderBreakdown.baseUnitHour',
    'line-item/day': 'OrderBreakdown.baseUnitDay',
    'line-item/night': 'OrderBreakdown.baseUnitNight',
    'line-item/item': 'OrderBreakdown.baseUnitQuantity',
  };
  const summaryQuantityRaw = unitLineItem?.quantity;
  const summaryQuantity =
    summaryQuantityRaw != null
      ? Number(
          typeof summaryQuantityRaw?.valueOf === 'function'
            ? summaryQuantityRaw.valueOf()
            : summaryQuantityRaw
        )
      : null;
  // Discount-code line item (customer side) shown explicitly in the summary view.
  const summaryDiscountItem =
    showSummaryOnly && isCustomer
      ? lineItems.find(
          i =>
            i.code === LINE_ITEM_DISCOUNT_CODE &&
            !i.reversal &&
            i.includeFor &&
            i.includeFor.includes('customer')
        )
      : null;
  // The weekday and first-booking discounts are also kept visible in the summary
  // (see the LineItem*Maybe pair rendered below), so they must be added back too —
  // otherwise summaryUnitPrice silently already nets them out of payinTotal while
  // the discount row is shown as if it were a separate deduction from that price.
  const findSummaryDiscount = code =>
    showSummaryOnly && isCustomer
      ? lineItems.find(
          i => i.code === code && !i.reversal && i.includeFor && i.includeFor.includes('customer')
        )
      : null;
  const summaryWeekdayDiscountItem = findSummaryDiscount(LINE_ITEM_WEEKDAY_BOOKING_DISCOUNT);
  const summaryFirstBookingDiscountItem = findSummaryDiscount(
    LINE_ITEM_FIRST_BOOKING_WELCOME_DISCOUNT
  );
  const summaryDiscountAmt =
    (summaryDiscountItem?.lineTotal?.amount || 0) +
    (summaryWeekdayDiscountItem?.lineTotal?.amount || 0) +
    (summaryFirstBookingDiscountItem?.lineTotal?.amount || 0); // negative

  // The commission is never its own row in this collapsed view — every price
  // shown (base + discounts) already has it baked in, so the "-20%" a guest
  // sees is genuinely 20% of the base row above it, not of a hidden
  // pre-commission figure. Reconstruct the true raw base (no discounts, no
  // commission), mark it up by the commission rate, then apply each discount
  // percentage to that gross figure in the server's order (weekday, then
  // first-booking) — see buildCommissionInclusiveDiscountChain for why this is
  // exact, not an approximation, and lands on the same real payinTotal.
  const summaryCommissionItem =
    showSummaryOnly && isCustomer
      ? lineItems.find(i => i.code === LINE_ITEM_CUSTOMER_COMMISSION && !i.reversal)
      : null;
  const summaryCommissionPct = summaryCommissionItem
    ? numericPercentage(summaryCommissionItem.percentage) || 0
    : 0;
  const summarySubtotalExclCommission =
    showSummaryOnly && isCustomer
      ? lineItems
          .filter(
            i =>
              !i.reversal &&
              i.includeFor &&
              i.includeFor.includes('customer') &&
              i.code !== LINE_ITEM_CUSTOMER_COMMISSION
          )
          .reduce((sum, i) => sum + (i.lineTotal?.amount || 0), 0)
      : 0;
  const summaryRawBaseAmount = summarySubtotalExclCommission - summaryDiscountAmt;
  const summaryWeekdayPct = summaryWeekdayDiscountItem
    ? Math.abs(numericPercentage(summaryWeekdayDiscountItem.percentage))
    : null;
  const summaryFirstBookingPct = summaryFirstBookingDiscountItem
    ? Math.abs(numericPercentage(summaryFirstBookingDiscountItem.percentage))
    : null;
  const summaryDiscountChain = buildCommissionInclusiveDiscountChain(
    summaryRawBaseAmount,
    summaryCommissionPct,
    [summaryWeekdayPct, summaryFirstBookingPct]
  );
  const [
    summaryWeekdayDiscountAmt,
    summaryFirstBookingDiscountAmt,
  ] = summaryDiscountChain.discountAmounts;

  // With itemizeSummary the "uso de piscina" extra gets its own row instead of
  // being folded into the hourly price. It takes its share of the same gross
  // figure, so hours + extra − discounts still adds up to payinTotal.
  const summaryPoolUseItem = itemizeSummary ? findSummaryDiscount(LINE_ITEM_POOL_USE_EXTRA) : null;
  const summaryPoolUseGrossAmount =
    summaryPoolUseItem && summaryRawBaseAmount > 0
      ? Math.round(
          (summaryDiscountChain.grossAmount * summaryPoolUseItem.lineTotal.amount) /
            summaryRawBaseAmount
        )
      : 0;

  // Los espacios interiores extra son un importe fijo por reserva, no por hora: van en su
  // propia fila (con la comisión incluida, como el resto del resumen) y se sacan de la base
  // antes de dividir por las horas. Si no, el precio por hora mostrado los llevaría dentro.
  const summaryIndoorSpaceRows =
    showSummaryOnly && isCustomer
      ? lineItems
          .filter(
            i =>
              isIndoorSpaceLineItem(i.code) &&
              !i.reversal &&
              i.includeFor &&
              i.includeFor.includes('customer')
          )
          .map(i => ({
            code: i.code,
            title: indoorSpaceTitleForLineItem(transaction?.listing, i.code),
            gross: Math.round(i.lineTotal.amount * (1 + summaryCommissionPct / 100)),
          }))
      : [];
  const summaryIndoorSpacesGross = summaryIndoorSpaceRows.reduce((sum, r) => sum + r.gross, 0);
  const summaryUnitGrossAmount =
    summaryDiscountChain.grossAmount - summaryPoolUseGrossAmount - summaryIndoorSpacesGross;

  const summaryUnitPrice =
    showSummaryOnly &&
    isCustomer &&
    summaryQuantity &&
    Number.isFinite(summaryQuantity) &&
    summaryQuantity > 0 &&
    transaction?.attributes?.payinTotal?.amount != null
      ? new Money(
          Math.round(summaryUnitGrossAmount / summaryQuantity),
          transaction.attributes.payinTotal.currency
        )
      : null;
  const summaryWeekdayDiscountFormatted =
    showSummaryOnly && summaryWeekdayDiscountItem
      ? formatMoney(
          intl,
          new Money(-summaryWeekdayDiscountAmt, transaction.attributes.payinTotal.currency)
        )
      : null;
  const summaryFirstBookingDiscountFormatted =
    showSummaryOnly && summaryFirstBookingDiscountItem
      ? formatMoney(
          intl,
          new Money(-summaryFirstBookingDiscountAmt, transaction.attributes.payinTotal.currency)
        )
      : null;
  const summaryLineLabelMessageId = lineItemUnitType
    ? baseUnitMessageIdByCode[lineItemUnitType]
    : null;
  const summaryLineLabel =
    summaryUnitPrice && summaryLineLabelMessageId ? (
      <FormattedMessage
        id={summaryLineLabelMessageId}
        values={{
          unitPrice: formatMoney(intl, summaryUnitPrice),
          quantity: summaryQuantity,
        }}
      />
    ) : null;

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  return (
    <div className={classes}>
      {!showSummaryOnly ? (
        <>
          <LineItemBookingPeriod
            booking={booking}
            code={lineItemUnitType}
            dateType={dateType}
            timeZone={timeZone}
            lineItems={lineItems}
            guestCountOverride={groupOverride}
            extraHours={extraHours}
          />

          <LineItemBasePriceMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />
          <LineItemPoolUseExtraMaybe lineItems={lineItems} intl={intl} />
          <LineItemIndoorSpacesMaybe
            lineItems={lineItems}
            listing={transaction?.listing}
            isProvider={isProvider}
            intl={intl}
          />
          <LineItemWeekdayBookingDiscountMaybe lineItems={lineItems} intl={intl} />
          <LineItemFirstBookingDiscountMaybe lineItems={lineItems} intl={intl} />
          <LineItemDiscountCodeMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />
          <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
          <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
          <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

          {/* Ampliaciones integradas como líneas más del pedido (a precio base;
              la comisión del bañista se itemiza aparte, como en la reserva base). */}
          {upgradeRows.map(row => (
            <div key={`upgrade-${row.key}`} className={css.lineItem}>
              <span className={css.itemLabel}>{row.label}</span>
              <span className={css.itemValue}>{formatMoney(intl, row.amounts.base)}</span>
            </div>
          ))}

          {hasUpgrades && isProvider ? (
            <>
              {combinedProviderSubtotal ? (
                <>
                  <hr className={css.totalDivider} />
                  <div className={css.subTotalLineItem}>
                    <span className={css.itemLabel}>
                      <FormattedMessage id="OrderBreakdown.subTotal" />
                    </span>
                    <span className={css.itemValue}>
                      {formatMoney(intl, combinedProviderSubtotal)}
                    </span>
                  </div>
                </>
              ) : null}
              <div className={css.lineItem}>
                <span className={css.itemLabel}>
                  <FormattedMessage
                    id="OrderBreakdown.commission"
                    values={{ marketplaceName, role: 'provider' }}
                  />
                </span>
                <span className={css.itemValue}>
                  {formatMoney(intl, combinedProviderCommission)}
                </span>
              </div>
              <LineItemHostInsuranceMaybe
                lineItems={lineItems}
                isProvider={isProvider}
                intl={intl}
              />
              <LineItemHostInsuranceDiscountMaybe
                lineItems={lineItems}
                isProvider={isProvider}
                intl={intl}
              />
            </>
          ) : hasUpgrades && isCustomer ? (
            !hideCustomerFeeDetails ? (
              <div className={css.lineItem}>
                <span className={css.itemLabel}>
                  <FormattedMessage
                    id="OrderBreakdown.commission"
                    values={{ marketplaceName, role: 'customer' }}
                  />
                </span>
                <span className={css.itemValue}>
                  {formatMoney(intl, combinedCustomerCommission)}
                </span>
              </div>
            ) : null
          ) : (
            <>
              <LineItemSubTotalMaybe
                lineItems={lineItems}
                isCustomer={isCustomer}
                code={lineItemUnitType}
                userRole={userRole}
                intl={intl}
                marketplaceCurrency={currency}
              />
              <LineItemRefundMaybe
                lineItems={lineItems}
                intl={intl}
                marketplaceCurrency={currency}
              />

              {!hideCustomerFeeDetails ? (
                <LineItemCustomerCommissionMaybe
                  lineItems={lineItems}
                  isCustomer={isCustomer}
                  marketplaceName={marketplaceName}
                  intl={intl}
                />
              ) : null}
              {!hideCustomerFeeDetails ? (
                <LineItemCustomerCommissionRefundMaybe
                  lineItems={lineItems}
                  isCustomer={isCustomer}
                  marketplaceName={marketplaceName}
                  intl={intl}
                />
              ) : null}

              <LineItemProviderCommissionMaybe
                lineItems={lineItems}
                isProvider={isProvider}
                marketplaceName={marketplaceName}
                intl={intl}
                isExternalBooking={!!transaction?.attributes?.metadata?.externalBooking}
              />
              <LineItemProviderCommissionRefundMaybe
                lineItems={lineItems}
                isProvider={isProvider}
                marketplaceName={marketplaceName}
                intl={intl}
              />
              <LineItemHostInsuranceMaybe
                lineItems={lineItems}
                isProvider={isProvider}
                intl={intl}
              />
              <LineItemHostInsuranceDiscountMaybe
                lineItems={lineItems}
                isProvider={isProvider}
                intl={intl}
              />
            </>
          )}
        </>
      ) : null}

      {showSummaryOnly && summaryLineLabel ? (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>{summaryLineLabel}</span>
          {itemizeSummary || summaryIndoorSpaceRows.length ? (
            <span className={css.itemValue}>
              {formatMoney(
                intl,
                new Money(summaryUnitGrossAmount, transaction.attributes.payinTotal.currency)
              )}
            </span>
          ) : null}
        </div>
      ) : null}

      {showSummaryOnly && summaryPoolUseGrossAmount ? (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="OrderBreakdown.poolUseExtra" />
          </span>
          <span className={css.itemValue}>
            {formatMoney(
              intl,
              new Money(summaryPoolUseGrossAmount, transaction.attributes.payinTotal.currency)
            )}
          </span>
        </div>
      ) : null}

      {summaryLineLabel
        ? summaryIndoorSpaceRows.map(row => (
            <div key={`summary-${row.code}`} className={css.lineItem}>
              <span className={css.itemLabel}>
                {row.title
                  ? intl.formatMessage(
                      { id: 'OrderBreakdown.indoorSpaceExtraNamed' },
                      { title: row.title }
                    )
                  : intl.formatMessage({ id: 'OrderBreakdown.indoorSpaceExtra' })}
              </span>
              <span className={css.itemValue}>
                {formatMoney(
                  intl,
                  new Money(row.gross, transaction.attributes.payinTotal.currency)
                )}
              </span>
            </div>
          ))
        : null}

      {showSummaryOnly && summaryDiscountItem ? (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="OrderBreakdown.discountCode" />
          </span>
          <span className={css.itemValue}>{formatMoney(intl, summaryDiscountItem.lineTotal)}</span>
        </div>
      ) : null}

      {/* En la vista resumida, las ampliaciones se listan en bruto (con comisión
          incluida, sin línea de tarifa) para que cuadren con el "Precio total". */}
      {showSummaryOnly && hasUpgrades
        ? upgradeRows.map(row => (
            <div key={`summary-upgrade-${row.key}`} className={css.lineItem}>
              <span className={css.itemLabel}>{row.label}</span>
              <span className={css.itemValue}>{formatMoney(intl, row.amounts.guest)}</span>
            </div>
          ))
        : null}

      {/* Keep discounts visible even in the collapsed summary so the customer sees
          the saving. Unlike the detailed view (LineItem*Maybe below, used when
          the commission has its own row), these show the commission-inclusive,
          gross-based amount computed above — genuinely a % of the base row shown
          above them, not of the server's raw pre-commission lineTotal. */}
      {showSummaryOnly && summaryWeekdayDiscountItem && summaryWeekdayDiscountFormatted ? (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage
              id="OrderBreakdown.weekdayBookingDiscount"
              values={{ percent: summaryWeekdayPct }}
            />
          </span>
          <span className={css.itemValue}>{summaryWeekdayDiscountFormatted}</span>
        </div>
      ) : null}
      {showSummaryOnly &&
      summaryFirstBookingDiscountItem &&
      summaryFirstBookingDiscountFormatted ? (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="OrderBreakdown.firstBookingWelcomeDiscount" />
          </span>
          <span className={css.itemValue}>{summaryFirstBookingDiscountFormatted}</span>
        </div>
      ) : null}

      {beforeTotal}

      {hasUpgrades && (isProvider ? combinedProviderNet : combinedCustomerTotal) ? (
        <>
          <hr className={css.totalDivider} />
          <div className={css.lineItemTotal}>
            <div className={css.totalLabel}>
              {isProvider ? (
                <FormattedMessage id={providerTotalMessageId} />
              ) : (
                <FormattedMessage id="OrderBreakdown.total" />
              )}
            </div>
            <div className={css.totalPrice}>
              {formatMoney(intl, isProvider ? combinedProviderNet : combinedCustomerTotal)}
            </div>
          </div>
        </>
      ) : (
        <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />
      )}

      {isCustomer && afterTotal ? <div className={css.afterTotal}>{afterTotal}</div> : null}

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}

      {isCustomer && showOnlyTotalByDefault && !hideBreakdownToggle ? (
        <button
          type="button"
          className={css.toggleBreakdownButton}
          onClick={() => setShowBreakdownDetails(prev => !prev)}
        >
          <FormattedMessage
            id={
              showBreakdownDetails
                ? 'OrderBreakdown.hideBookingBreakdown'
                : 'OrderBreakdown.showBookingBreakdown'
            }
          />
        </button>
      ) : null}
    </div>
  );
};

OrderBreakdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  booking: null,
  dateType: null,
  afterTotal: null,
  beforeTotal: null,
  showOnlyTotalByDefault: false,
  hideBreakdownToggle: false,
  itemizeSummary: false,
};

OrderBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  booking: propTypes.booking,
  dateType: propTypes.dateType,
  afterTotal: node,
  // Rendered right above the total (e.g. the checkout's discount-code row).
  beforeTotal: node,
  showOnlyTotalByDefault: bool,
  hideBreakdownToggle: bool,
  // Summary view only: show each row's amount and give the "uso de piscina"
  // extra its own row (checkout). Off, the summary keeps its label-only rows.
  itemizeSummary: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OrderBreakdown = injectIntl(OrderBreakdownComponent);

OrderBreakdown.displayName = 'OrderBreakdown';

export default OrderBreakdown;
